/**
 * EndpointMatrixHeatmap - dashboard Area x (ASN | country) heatmap.
 * Toggle the dimension and the metric (total vs high-risk). Reveals
 * concentrated area patterns that are invisible in flat top-N tables.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { fmtNumber } from "@/lib/format";
import { countryName } from "@/config/geo";
import { ENDPOINT_AREAS, endpointAreaLabel } from "@/config/filters";

type Dim = "asn" | "country";
type Metric = "total" | "highRisk";
type AreaRow = { endpointGroup: string; dim: string; total: number; highRisk: number };

export function EndpointMatrixHeatmap() {
  const [dim, setDim] = useState<Dim>("country");
  const [metric, setMetric] = useState<Metric>("highRisk");
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();

  const q = useQuery({
    queryKey: ["endpoint-matrix", dim, filters, tick],
    queryFn: ({ signal }) => api.endpointMatrix(filters, dim, signal),
  });

  const rows = q.data?.data?.rows ?? [];

  const { groups, dims, cellMap, max } = useMemo(() => {
    const areaRows = [...rows.reduce((map, row) => {
      const area = endpointAreaLabel(row.endpointGroup);
      const key = area + "\x00" + row.dim;
      const current = map.get(key) ?? { endpointGroup: area, dim: row.dim, total: 0, highRisk: 0 };
      current.total += row.total;
      current.highRisk += row.highRisk;
      map.set(key, current);
      return map;
    }, new Map<string, AreaRow>()).values()];

    const dimCount = new Map<string, number>();
    for (const row of areaRows) dimCount.set(row.dim, (dimCount.get(row.dim) ?? 0) + row[metric]);

    const dimsSorted = [...dimCount.entries()].sort((a, b) => b[1] - a[1]).slice(0, 16).map(([d]) => d);
    const groupSet = new Set<string>(areaRows.map((row) => row.endpointGroup));
    const groupsOrdered = ENDPOINT_AREAS.map((area) => area.label).filter((label) => groupSet.has(label));
    for (const group of groupSet) if (!groupsOrdered.includes(group)) groupsOrdered.push(group);

    const cm = new Map<string, number>();
    let m = 0;
    for (const row of areaRows) {
      const key = row.endpointGroup + "\x00" + row.dim;
      const value = row[metric];
      cm.set(key, value);
      if (dimsSorted.includes(row.dim) && groupsOrdered.includes(row.endpointGroup) && value > m) m = value;
    }
    return { groups: groupsOrdered, dims: dimsSorted, cellMap: cm, max: Math.max(1, m) };
  }, [rows, metric]);

  return (
    <Card>
      <CardHeader
        title="Area matrix"
        subtitle="Where each area's traffic concentrates by network or country. Toggle the dimension and the metric."
        action={
          <div className="flex gap-2 text-xs">
            <Toggle value={dim}    onChange={setDim}    options={[["country", "Country"], ["asn", "ASN"]]} />
            <Toggle value={metric} onChange={setMetric} options={[["highRisk", "High-risk"], ["total", "Total"]]} />
          </div>
        }
      />
      {q.isLoading ? (
        <Skeleton className="h-72" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : groups.length === 0 || dims.length === 0 ? (
        <EmptyState title="No area activity in this window." />
      ) : (
        <div className="overflow-x-auto">
          <div
            className="inline-grid gap-px"
            style={{ gridTemplateColumns: `120px repeat(${dims.length}, minmax(56px, 1fr))` }}
          >
            <div />
            {dims.map((d) => (
              <div key={"col-" + d} className="px-1 pb-1 text-center text-[10px] text-fg-subtle" title={dim === "country" ? countryName(d) : `AS${d}`}>
                {dim === "country" ? d : "AS" + d}
              </div>
            ))}
            {groups.map((group) => (
              <RowR key={group} g={group} dims={dims} cellMap={cellMap} max={max} dim={dim} metric={metric} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function RowR({ g, dims, cellMap, max, dim, metric }: {
  g: string; dims: string[]; cellMap: Map<string, number>; max: number; dim: Dim; metric: Metric;
}) {
  return (
    <>
      <div className="truncate pr-2 py-1 text-right text-[11px] text-fg" title={g}>{g}</div>
      {dims.map((d) => {
        const value = cellMap.get(g + "\x00" + d) ?? 0;
        const t = value / max;
        const color = metric === "highRisk" ? "var(--sev-high)" : "var(--accent)";
        return (
          <div
            key={g + "-" + d}
            title={`${g} - ${dim === "country" ? countryName(d) : "AS" + d}: ${fmtNumber(value)} ${metric === "highRisk" ? "high-risk events" : "events"}`}
            style={{ backgroundColor: `rgb(${color} / ${0.05 + t * 0.85})` }}
            className="grid h-7 place-items-center text-[10px] text-fg"
          >
            {value > 0 ? fmtNumber(value, { compact: true }) : ""}
          </div>
        );
      })}
    </>
  );
}

function Toggle<T extends string>({ value, onChange, options }: {
  value: T; onChange: (value: T) => void; options: [T, string][];
}) {
  return (
    <div className="inline-flex overflow-hidden rounded border border-border">
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-2 py-1 ${value === v ? "bg-accent/20 text-accent" : "text-fg-muted hover:bg-surface-3"}`}
        >{label}</button>
      ))}
    </div>
  );
}
